import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { db, functions, auth } from "../lib/firebase";
import { collection, addDoc, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { MediaType, UploadProgress } from "../types/media";
import { clearCache } from "../utils/sessionCache";

const FIREBASE_FUNCTION_URL = 'https://us-central1-edumi-4dfbe.cloudfunctions.net/uploadToR2';

export function useMediaUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const uploadMedia = async (
    courseId: string,
    file: File,
    mediaType: MediaType,
    lessonId?: string
  ): Promise<{ mediaId: string; url: string; jobId?: string }> => {
    setUploading(true);
    setProgress({
      fileName: file.name,
      progress: 0,
      status: "uploading",
    });

    try {
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error("กรุณา login เข้าระบบก่อนอัปโหลดไฟล์");
      }

      const idToken = await currentUser.getIdToken();
      const safeFileName = file.name && file.name.trim() !== "" ? file.name : `file-${Date.now()}`;

      // Upload to Firebase Function (R2)
      console.log('📤 Starting upload to Firebase Function (R2)');
      console.log('  File name:', file.name);
      console.log('  File size:', file.size, 'bytes', `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      console.log('  File type:', file.type);
      console.log('  Media type:', mediaType);
      const result = await uploadToFirebaseFunction(file, safeFileName, idToken, courseId, lessonId);
      const key = result.key;
      const jobId = result.jobId;
      const publicUrl = result.publicUrl;

      if (!key) {
        throw new Error("Upload failed - no key returned");
      }

      setProgress({
        fileName: file.name,
        progress: 100,
        status: "uploading",
      });

      // Video job is already created by Cloud Run if it's a video

      const mediaRef = collection(db, "courses", courseId, "media");
      const mediaDoc = await addDoc(mediaRef, {
        type: mediaType,
        url: publicUrl,
        key,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        downloadable: mediaType === "file",
        jobId: jobId || null,
        uploadedBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });

      setProgress({
        fileName: file.name,
        progress: 100,
        status: "success",
      });

      setUploading(false);
      
      // Clear storage cache to force refresh
      if (currentUser) {
        clearCache(`user_storage_${currentUser.uid}`);
        console.log('✅ Storage cache cleared - quota will refresh');
      }
      
      setTimeout(() => {
        setProgress(null);
      }, 1000);
      
      console.log("Upload complete:", { mediaId: mediaDoc.id, url: publicUrl, jobId });
      return { 
        mediaId: mediaDoc.id, 
        url: publicUrl || '',
        jobId
      };
    } catch (error: any) {
      console.error("Upload error:", error);
      setProgress({
        fileName: file.name,
        progress: 0,
        status: "error",
        error: error.message || "Upload failed",
      });
      setUploading(false);
      throw error;
    }
  };

  const uploadToFirebaseFunction = async (
    file: File,
    fileName: string,
    idToken: string,
    courseId?: string,
    lessonId?: string
  ): Promise<{ publicUrl: string | null; key: string; jobId?: string }> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', fileName);
      if (courseId) formData.append('courseId', courseId);
      if (lessonId) formData.append('lessonId', lessonId);
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setProgress({
            fileName: file.name,
            progress: percent,
            status: "uploading",
          });
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('✅ Upload successful:', response);
            resolve(response);
          } catch (error) {
            console.error('❌ Failed to parse response:', xhr.responseText);
            reject(new Error('Failed to parse response'));
          }
        } else {
          console.error('❌ Upload failed with status:', xhr.status);
          console.error('Response:', xhr.responseText);
          let errorMessage = `อัปโหลดล้มเหลว (${xhr.status})`;
          try {
            const errorData = JSON.parse(xhr.responseText);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            // Use raw response if not JSON
            errorMessage = xhr.responseText || errorMessage;
          }
          reject(new Error(errorMessage));
        }
      });
      
      xhr.addEventListener('error', (e) => {
        console.error('Network error:', e);
        reject(new Error('Network error - please check your connection'));
      });
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
      xhr.addEventListener('timeout', () => reject(new Error('Upload timeout - file may be too large')));
      
      xhr.open('POST', FIREBASE_FUNCTION_URL);
      xhr.timeout = 900000; // 15 minutes timeout for large video files (up to 2GB)
      xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);
      xhr.send(formData);
    });
  };


  return {
    uploadMedia,
    uploading,
    progress,
  };
}
